import { useState, useEffect } from "react";
import { postsService } from "../services/postsService";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import {
  Post,
  CreatePostRequest,
  UpdatePostRequest,
} from "@/core/types/postTypes";
import { postCacheUtils } from "./usePostsQuery";
import { PostItem } from "@/core/Post/PostItem.model";
import { UserRole } from "@/services/urlService";
import type { CropSelection } from "../MediaCropModal";

interface UsePostCreatorOptions {
  editingPost?: Post | null;
  initialMediaItems?: MediaItem[];
  onSuccess?: (postId: string, response: any) => void;
  onError?: (error: string) => void;
}

// Rects carry the pixel space they were measured in (CropSelection) —
// the crop UI displays a downscaled variant, so a bare rect is ambiguous
// and the backend rescales via sourceWidth/sourceHeight at prep time.
type CropMap = Record<number, CropSelection>;

export const usePostCreator = (
  clientId: string | null,
  options: UsePostCreatorOptions = {},
) => {
  const { editingPost } = options;
  const isEditMode = Boolean(editingPost);

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMediaItems, setSelectedMediaItems] = useState<MediaItem[]>([]);
  const [isCarousel, setIsCarousel] = useState(false);
  // Keyed by MediaItem.id — manual 4:5 crops chosen via the crop modal.
  // Items not in the map fall back to the backend's centered crop.
  const [crops, setCrops] = useState<CropMap>({});

  useEffect(() => {
    if (editingPost) {
      setIsCarousel(editingPost.isCarousel);

      if (editingPost.mediaItems && editingPost.mediaItems.length > 0) {
        const sortedItems = MediaItem.sortByPosition(editingPost.mediaItems);
        setSelectedMediaItems(sortedItems);
        // Seed the crop map from any rect already stored on the
        // post-media junction row. Legacy rects without source dims are
        // skipped: their pixel space is unknown (pre-fix rects were
        // measured on the downscaled display variant), so re-seeding
        // them would show a misleading frame.
        const seeded: CropMap = {};
        for (const item of sortedItems) {
          const rect = item.backendData.cropRect;
          if (rect && rect.sourceWidth && rect.sourceHeight) {
            seeded[item.id] = {
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height,
              sourceWidth: rect.sourceWidth,
              sourceHeight: rect.sourceHeight,
            };
          }
        }
        setCrops(seeded);
      } else {
        setSelectedMediaItems([]);
        setCrops({});
      }
    } else if (options.initialMediaItems?.length) {
      setSelectedMediaItems(options.initialMediaItems);
      setIsCarousel(options.initialMediaItems.length > 1);
      setCrops({});
    } else {
      setSelectedMediaItems([]);
      setIsCarousel(false);
      setCrops({});
    }

    setError(null);
  }, [editingPost, options.initialMediaItems]);

  const setCropForItem = (mediaItemId: number, rect: CropSelection | null) => {
    setCrops((prev) => {
      if (rect === null) {
        if (!(mediaItemId in prev)) return prev;
        const next = { ...prev };
        delete next[mediaItemId];
        return next;
      }
      return { ...prev, [mediaItemId]: rect };
    });
  };

  const buildImagePayload = (items: MediaItem[]) =>
    items.map((item) => {
      const base = item.toCreatePostImage();
      const rect = crops[item.id];
      return rect ? { ...base, cropRect: rect } : base;
    });

  const validateForm = (): string | null => {
    if (selectedMediaItems.length === 0) {
      return "At least one image is required";
    }
    if (isCarousel && selectedMediaItems.length > 10) {
      return "Carousel posts can have maximum 10 images";
    }
    const hasVideo = selectedMediaItems.some((item) => item.isPostVideo());
    if (hasVideo && selectedMediaItems.length < 2) {
      return "A post with video must have at least 2 items";
    }
    return null;
  };

  const generatePostId = (): string => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `post-${timestamp}-${random}`;
  };

  const savePost = async (): Promise<boolean> => {
    if (!clientId) {
      const errorMsg = "No client selected";
      setError(errorMsg);
      return false;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return false;
    }

    setIsCreating(true);
    setError(null);

    try {
      if (isEditMode && editingPost) {
        const updateData: UpdatePostRequest = {
          postId: editingPost.id,
          images: buildImagePayload(selectedMediaItems),
        };

        // Use postsService directly
        const response = await postsService.updatePost(
          clientId,
          editingPost.id,
          updateData
        );

        // Update React Query cache
        const postItem = PostItem.fromLegacyPost(response.post, UserRole.ADMIN, clientId);
        postCacheUtils.upsertPost(clientId, postItem);

        options.onSuccess?.(editingPost.id, { success: true });
        return true;
      } else {
        const postId = generatePostId();
        const postData: CreatePostRequest = {
          groupName: null,
          hashtags: null,
          caption: null,
          postId: postId,
          postType: "standard",
          images: buildImagePayload(selectedMediaItems),
        };

        // Use postsService directly
        const result = await postsService.createOrGetPost(clientId, postData);

        // Update React Query cache
        const postItem = PostItem.fromLegacyPost(result.post, UserRole.ADMIN, clientId);
        postCacheUtils.upsertPost(clientId, postItem);

        options.onSuccess?.(postId, { success: true, post: result.post });
        return true;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : `Failed to ${isEditMode ? "update" : "create"} post`;
      setError(errorMessage);
      options.onError?.(errorMessage);
      return false;
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    if (!isEditMode) {
      setSelectedMediaItems([]);
      setIsCarousel(false);
      setCrops({});
    }
    setError(null);
  };

  const reorderMediaItems = async (mediaOrder: { id: number; position: number }[]) => {
    if (!clientId || !editingPost) {
      throw new Error('Cannot reorder media items: missing client ID or editing post');
    }

    try {
      const result = await postsService.reorderPostMedia(
        clientId,
        editingPost.id,
        mediaOrder
      );
      
      // Update the local state with the reordered media items from backend response
      if (result.post.mediaItems) {
        const sortedItems = MediaItem.sortByPosition(result.post.mediaItems);
        setSelectedMediaItems(sortedItems);
      }
      
      return result;
    } catch (error) {
      console.error('Failed to reorder media items:', error);
      throw error;
    }
  };

  return {
    selectedMediaItems,
    setSelectedMediaItems,
    isCarousel,
    setIsCarousel,
    crops,
    setCropForItem,
    savePost,
    resetForm,
    generatePostId,
    reorderMediaItems,
    isCreating,
    error,
    setError,
    isEditMode,
    editingPost,
    clientId,
    isValid: validateForm() === null,
    imageCount: selectedMediaItems.length,
    maxImages: isCarousel ? 10 : 1,
    formData:
      isEditMode && editingPost
        ? {
            postId: editingPost.id,
            groupName: editingPost.groupName || "",
            postType: editingPost.postType,
            caption: editingPost.caption || "",
            hashtags: editingPost.hashtags || "",
          }
        : {
            postId: "",
            groupName: "",
            postType: "standard" as const,
            caption: "",
            hashtags: "",
          },
    updateFormData: () => {},
  };
};
